import * as React from "react";
import { KeyboardEventKeys } from "../../utils/keyboardEventKeys";
import { setInnerHtml } from "../../utils/helpers";

export interface ITabComponentProps {
  tabDescription: string;
  tabs: Tab[];
  isCampaign?: boolean;
}

export interface Tab {
  heading: string;
  body: string;
  selected: boolean;
  tabId: string;
  panelId: string;
}

export interface ITabComponentState {
  tabs: Tab[];
  scrollPosition: number;
  maxScrollPosition: number;
  darkScrollButton: boolean;
}

export class TabComponent extends React.Component<
  ITabComponentProps,
  ITabComponentState
> {
  tabContainerRef = React.createRef<HTMLDivElement>();

  constructor(props: ITabComponentProps) {
    super(props);

    this.state = {
      tabs: props.tabs,
      scrollPosition: 0,
      maxScrollPosition: 0,
      darkScrollButton: false,
    };
  }

  componentDidMount() {
    this.updateMaxScrollPosition();
    window.addEventListener("resize", this.updateMaxScrollPosition);
    const container = this.tabContainerRef.current;
    if (container) {
      container.addEventListener("scroll", this.handleScroll);
    }
    const bodyHasDarkBlueClass = document.body.classList.contains(
      "campaign-page-new__darkblue"
    );
    if (bodyHasDarkBlueClass) {
      this.setState({ darkScrollButton: true });
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateMaxScrollPosition);
    const container = this.tabContainerRef.current;
    if (container) {
      container.removeEventListener("scroll", this.handleScroll);
    }
  }

  componentDidUpdate(prevProps: ITabComponentProps) {
    if (prevProps.tabs !== this.props.tabs) {
      this.updateMaxScrollPosition();
    }
  }

  hideAllPanels = (tabs: Tab[]) => {
    tabs.forEach((tab) => {
      tab.selected = false;
    });
  };

  openTab = (clickedTab: Tab, e: React.MouseEvent) => {
    e.preventDefault();
    const tabs = this.state.tabs.slice();
    this.hideAllPanels(tabs);
    const selectedTab = tabs.find((x) => x.tabId === clickedTab.tabId);
    if (selectedTab) {
      selectedTab.selected = true;
      this.setState({ tabs }, () => {
        this.centerTab(clickedTab.tabId);
      });
    }
  };

  centerTab = (tabId: string) => {
    const container = this.tabContainerRef.current;
    const tab = document.getElementById(tabId);
    if (!container || !tab) return;

    const containerRect = container.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();

    const containerMidpoint = containerRect.left + containerRect.width / 2;
    const tabMidpoint = tabRect.left + tabRect.width / 2;

    const scrollOffset = tabMidpoint - containerMidpoint;
    const newScrollPosition = container.scrollLeft + scrollOffset;

    container.scrollTo({
      left: newScrollPosition,
      behavior: "smooth",
    });
  };

  navigateByKeyDown = (tab: Tab, e: React.KeyboardEvent) => {
    if (e.shiftKey) return;

    e.preventDefault();

    if (e.key === KeyboardEventKeys.tab)
      return document.getElementById(tab.panelId).focus();
    if (e.key !== KeyboardEventKeys.home && e.key !== KeyboardEventKeys.end)
      return;

    let targetIndex = 0;
    const tabs = this.state.tabs.slice(0);
    this.hideAllPanels(tabs);

    if (e.key === KeyboardEventKeys.end) targetIndex = tabs.length - 1;
    tabs[targetIndex].selected = true;
    document.getElementById(tabs[targetIndex].tabId).focus();
    this.setState({ tabs }, () => {
      this.centerTab(tabs[targetIndex].tabId);
    });
  };

  navigateByKeyUp = (e: React.KeyboardEvent) => {
    e.preventDefault();

    if (
      e.key !== KeyboardEventKeys.arrowLeft &&
      e.key !== KeyboardEventKeys.arrowRight
    ) {
      return;
    }

    const tabs = this.state.tabs.slice(0);
    let targetIndex = tabs.findIndex((x) => x.selected);
    this.hideAllPanels(tabs);

    switch (e.key) {
      case KeyboardEventKeys.arrowLeft:
        targetIndex--;
        break;
      case KeyboardEventKeys.arrowRight:
        targetIndex++;
        break;
    }

    if (targetIndex < 0) targetIndex = tabs.length - 1;
    if (targetIndex > tabs.length - 1) targetIndex = 0;

    tabs[targetIndex].selected = true;
    document.getElementById(tabs[targetIndex].tabId).focus();
    this.setState({ tabs }, () => {
      this.centerTab(tabs[targetIndex].tabId);
    });
  };

  updateMaxScrollPosition = () => {
    const container = this.tabContainerRef.current;
    if (!container) return;
    const maxScrollPosition = container.scrollWidth - container.clientWidth;
    this.setState({ maxScrollPosition });
  };

  handleScroll = () => {
    const container = this.tabContainerRef.current;
    if (!container) return;
    const scrollPosition = container.scrollLeft;
    this.setState({ scrollPosition });
  };

  scrollTabs = (direction: "left" | "right") => {
    const scrollStep = 300;
    const container = this.tabContainerRef.current;
    if (!container) return;
    let newPosition = this.state.scrollPosition;

    if (direction === "left") {
      newPosition -= scrollStep;
    } else {
      newPosition += scrollStep;
    }

    newPosition = Math.max(
      0,
      Math.min(newPosition, this.state.maxScrollPosition)
    );

    container.scrollTo({
      left: newPosition,
      behavior: "smooth",
    });

    this.setState({ scrollPosition: newPosition });
  };

  renderTab = (tab: Tab) => {
    return (
      <button
        className="tabcomponent__tab"
        key={tab.tabId}
        id={tab.tabId}
        onClick={(e) => this.openTab(tab, e)}
        onKeyUp={this.navigateByKeyUp}
        onKeyDown={(e) => this.navigateByKeyDown(tab, e)}
        type="button"
        role="tab"
        aria-selected={tab.selected}
        aria-controls={`${tab.panelId}`}
        tabIndex={tab.selected ? 0 : -1}
      >
        {tab.heading}
      </button>
    );
  };

  renderTabPanel = (tab: Tab) => {
    return (
      <div
        className="tabcomponent__panel"
        key={tab.panelId}
        id={tab.panelId}
        tabIndex={tab.selected ? 0 : -1}
        role="tabpanel"
        aria-labelledby={tab.tabId}
        dangerouslySetInnerHTML={setInnerHtml(tab.body)}
        hidden={!tab.selected}
      />
    );
  };

  render() {
    const { tabDescription } = this.props;
    const { tabs, maxScrollPosition, scrollPosition, darkScrollButton } =
      this.state;
    const leftButtonDisabled = scrollPosition === 0;
    const rightButtonDisabled = scrollPosition >= maxScrollPosition;
    const scrollButtonClass = darkScrollButton ? "dark-scroll-button" : "";

    return (
      <div className="tabcomponent">
        {this.props.isCampaign ? (
          <div className="container">
            <div className="row">
              <div className="col-12">
                <div className={`scroll-button arrowLeft ${scrollButtonClass}`}>
                  <button
                    className="scroll-button__left"
                    onClick={() => this.scrollTabs("left")}
                    disabled={leftButtonDisabled}
                  ></button>
                </div>
                <div
                  role="tablist"
                  aria-label={tabDescription}
                  className={`tabcomponent__tabs ${
                    !rightButtonDisabled ? "marginRight" : ""
                  } ${!leftButtonDisabled ? "marginLeft" : ""}`}
                  ref={this.tabContainerRef}
                  onScroll={this.handleScroll}
                >
                  {tabs.map(this.renderTab)}
                </div>
                <div
                  className={`scroll-button arrowRight ${scrollButtonClass}`}
                >
                  <button
                    className="scroll-button__right"
                    onClick={() => this.scrollTabs("right")}
                    disabled={rightButtonDisabled}
                  ></button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            role="tablist"
            aria-label={tabDescription}
            className={`tabcomponent__tabs ${
              leftButtonDisabled ? "marginLeft" : "marginRight"
            }`}
          >
            {tabs.map(this.renderTab)}
          </div>
        )}

        {tabs.map(this.renderTabPanel)}
      </div>
    );
  }
}
